// Acceleration + enrichment engine. Phase F1.
//
// Spec §"Acceleration and enrichment logic" (lines 1294–1452). Treats
// acceleration as a privilege earned by low-support, transfer-validated,
// delayed-review-confirmed performance. When the next formal skill is
// developmentally too early, the engine enriches inside the current band
// instead of accelerating formally.
//
// Composition notes:
//   - `evaluateAccelerationEligibility` reads `durableMastery` (which
//     already encodes the delayed-review gate per Phase D) as the single
//     source of truth — §F1-Q1 resolution. We do NOT re-read
//     DelayedReviewEvidence here.
//   - `selectAccelerationAction` is intentionally a thin wrapper kept
//     for spec parity. Future hook point for telemetry / experiments
//     without touching the central decision engine (§F1-Q2 resolution).
//   - Blocker chain order matches spec lines 1342–1352 verbatim.
//
// Public surface consumed by:
//   - Phase F2 adaptationDecisionEngine.ts (priority 8: mastery + gates
//     → advance OR compactReview; priority 7: too-early → enrich)
//
// Phase F2 (decision engine) consumes the AccelerationEligibility shape
// to compose the final decision. F2 owns the priority ordering; F1 only
// answers "given these inputs, is acceleration safe?"

import type {
  AdaptationDecisionType,
  AgeBand,
  AttemptWindow,
  ContentSafetyState,
  DecisionBlockerCode,
  DecisionReasonCode,
  NextActivityPlan,
  ProgressionState,
  SkillMasteryState,
} from "./types";

interface AccelerationEligibility {
  eligible: boolean;
  action: "none" | "skip_easy_items" | "compact_review" | "advance" | "enrich_instead";
  reasonCodes: DecisionReasonCode[];
  blockerCodes: DecisionBlockerCode[];
}

/** Evaluate whether acceleration is safe. Walks the full blocker list
 * from spec lines 1342–1352. Any blocker forces `eligible: false`; if
 * the specific blocker is `nextFormalSkillTooEarly`, returns
 * `action: "enrich_instead"` so the caller routes to enrichment
 * instead of stalling on the current skill. */
export function evaluateAccelerationEligibility(
  _skillId: string,
  masteryState: SkillMasteryState,
  attemptWindow: AttemptWindow,
  progressionState: ProgressionState,
  candidateNextContentSafety: ContentSafetyState | null,
): AccelerationEligibility {
  const reasonCodes: DecisionReasonCode[] = [];
  const blockerCodes: DecisionBlockerCode[] = [];

  // §F1-Q1: durableMastery already encodes the delayed-review pass per
  // Phase D's masteryEvidence. Re-reading delayedReviewEvidence here
  // would split the source of truth.
  if (!masteryState.durableMastery) blockerCodes.push("BLOCK_NO_DELAYED_REVIEW");
  if (!masteryState.transferPassed) blockerCodes.push("BLOCK_NO_TRANSFER");
  if (attemptWindow.highHintRate > 0.4) blockerCodes.push("BLOCK_HIGH_HINT_USE");
  if (attemptWindow.h4Used) blockerCodes.push("BLOCK_H4_SUCCESS");
  if (attemptWindow.sameErrorLoop) blockerCodes.push("BLOCK_SAME_ERROR_LOOP");
  if (attemptWindow.readAloudOnlyReadingEvidence) {
    blockerCodes.push("BLOCK_AUDIO_SUPPORTED_READING");
  }
  if (attemptWindow.oralOnlyReadingEvidence) {
    blockerCodes.push("BLOCK_ORAL_VOCAB_FOR_READING");
  }
  if (
    candidateNextContentSafety &&
    !candidateNextContentSafety.independentPracticeAllowed
  ) {
    blockerCodes.push("BLOCK_CONTENT_NOT_LAUNCH_SAFE");
  }
  if (progressionState.nextFormalSkillTooEarly) {
    blockerCodes.push("BLOCK_NEXT_SKILL_TOO_EARLY");
  }

  if (blockerCodes.length > 0) {
    return {
      eligible: false,
      action: progressionState.nextFormalSkillTooEarly ? "enrich_instead" : "none",
      reasonCodes,
      blockerCodes,
    };
  }

  if (progressionState.compactReviewEligible) {
    reasonCodes.push("READY_FOR_COMPACT_REVIEW");
    return {
      eligible: true,
      action: "compact_review",
      reasonCodes,
      blockerCodes,
    };
  }

  reasonCodes.push("READY_FOR_ADVANCEMENT");
  return {
    eligible: true,
    action: "advance",
    reasonCodes,
    blockerCodes,
  };
}

/** Thin wrapper retained for spec parity (§F1-Q2). Future hook point
 * for telemetry / experiments without touching the decision engine. */
export function selectAccelerationAction(
  _skillId: string,
  progressionState: ProgressionState,
): AdaptationDecisionType {
  return progressionState.compactReviewEligible ? "compactReview" : "advance";
}

/** Build an enrichment plan when the next formal skill is too early.
 * Per spec lines 1441–1452: preserves challenge inside the current
 * skill family instead of pretending the child is ready to advance.
 * maxItems: 5 for ageBand 4.0-4.5, 6 otherwise. */
export function selectEnrichmentInstead(
  skillId: string,
  ageBand: AgeBand,
  _blockers: DecisionBlockerCode[],
): NextActivityPlan {
  return {
    decision: "enrich",
    skillId,
    nextSkillId: null,
    selectedActivityType: "enrichmentChallenge",
    selectedContentFile: "/src/curriculum/manifests/accelerationRules.json",
    selectedContentId: null,
    supportPlan: {
      allowedSupports: ["independent", "H1"],
      initialHintLevel: "H0",
      requireIndependentRetest: false,
      separateHandwritingChannel: true,
    },
    runPlan: {
      maxItems: ageBand === "4.0-4.5" ? 5 : 6,
      includeReviewCheckpoint: false,
      includeTransferTask: false,
      includeDelayedReviewItem: false,
    },
    scheduleFollowUpAt: null,
  };
}

/** Build a compact-review plan: start the next formal skill but keep
 * one short checkpoint on the prior skill. Per spec lines 1417–1438. */
export function compactReviewPlan(
  skillId: string,
  _masteryState: SkillMasteryState,
  nextSkillId: string | null,
): NextActivityPlan {
  return {
    decision: "compactReview",
    skillId,
    nextSkillId,
    selectedActivityType: "assessmentProbe",
    selectedContentFile: "/src/curriculum/manifests/accelerationRules.json",
    selectedContentId: null,
    supportPlan: {
      allowedSupports: ["independent", "H1"],
      initialHintLevel: "H0",
      requireIndependentRetest: false,
      separateHandwritingChannel: true,
    },
    runPlan: {
      maxItems: 5,
      includeReviewCheckpoint: true,
      includeTransferTask: false,
      includeDelayedReviewItem: false,
    },
    scheduleFollowUpAt: null,
  };
}
